import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, normalizeClass as _normalizeClass, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-y-2" };
const _hoisted_2 = {
    for: "date",
    class: "text-sm md:text-md"
};
const _hoisted_3 = {
    key: 0,
    class: "w-full p-2 text-xs text-gray-500 border border-gray-300 rounded-lg bg-gray-50"
};
const _hoisted_4 = ["value", "disabled", "placeholder"];
const _hoisted_5 = {
    key: 1,
    class: "text-xs text-red-400"
};
const _hoisted_6 = {
    key: 2,
    class: "text-xs text-red-400"
};
const _hoisted_7 = {
    key: 3,
    class: "text-xs text-red-400"
};
const _hoisted_8 = {
    key: 4,
    class: "text-xs text-gray-500"
};
const _hoisted_9 = {
    key: 5,
    class: "text-xs text-gray-500"
};
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { bookingValidationService } from '../services/booking-validation.service';
import { useDateAvailability } from '@domains/scheduling/composables/useDateAvailability';
import { useItineraryI18n } from '../composables/useItineraryI18n';
// =============================================================================
// PROPS & EMITS
// =============================================================================
export default /*@__PURE__*/ _defineComponent({
    __name: 'DateSelector',
    props: {
        modelValue: {},
        serviceId: { default: null },
        minDays: { default: 0 },
        maxDays: { default: 0 },
        readOnly: { type: Boolean, default: false },
        showError: { type: Boolean, default: false },
        errorMessage: { default: '' }
    },
    emits: ["update:modelValue", "change", "unavailable-date-selected"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // =============================================================================
        // COMPOSABLES
        // =============================================================================
        const { availabilityVersion, isDateAvailable, isLoading, ensureMonthLoaded, loadMonthAvailability, getMonthStatus, isMonthLoaded, clearAvailability, error: availabilityError, } = useDateAvailability();
        const { t } = useItineraryI18n();
        // =============================================================================
        // STATE
        // =============================================================================
        const showAvailabilityError = ref(false);
        const availabilityErrorMessage = ref('');
        const dateInputRef = ref(null);
        const flatpickrInstance = ref(null);
        const isLoadingMonth = ref(false);
        const hasLoadedAnyMonth = ref(false);
        let debounceTimer = null;
        // =============================================================================
        // COMPUTED
        // =============================================================================
        const minDate = computed(() => {
            if (props.minDays <= 0)
                return undefined;
            const date = bookingValidationService.getEarliestBookingDate(props.minDays);
            return formatDateForInput(date);
        });
        const maxDate = computed(() => {
            if (props.maxDays <= 0)
                return undefined;
            const date = bookingValidationService.getLatestBookingDate(props.maxDays);
            return formatDateForInput(date);
        });
        const bookingWindowMessage = computed(() => {
            if (props.minDays <= 0 && props.maxDays <= 0)
                return '';
            const parts = [];
            if (props.minDays > 0) {
                parts.push(t("itinerary.validation.min_days_window", { count: props.minDays }, "mínimo {count} día(s) de anticipación"));
            }
            if (props.maxDays > 0) {
                parts.push(t("itinerary.validation.max_days_window", { count: props.maxDays }, "máximo {count} días en el futuro"));
            }
            return t("itinerary.validation.window_label", { window: parts.join(', ') }, "Reserva: {window}");
        });
        // =============================================================================
        // WATCHERS
        // =============================================================================
        watch(() => props.serviceId, async (newServiceId) => {
            if (props.readOnly)
                return;
            clearAvailability();
            hasLoadedAnyMonth.value = false;
            if (newServiceId) {
                showAvailabilityError.value = false;
                availabilityErrorMessage.value = '';
                await loadInitialMonth(newServiceId);
            }
        }, { immediate: true });
        watch(availabilityVersion, () => {
            applyAvailabilityRules();
        }, { flush: 'post' });
        // =============================================================================
        // METHODS
        // =============================================================================
        function formatDateForInput(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        function handleInput(event) {
            const target = event.target;
            const value = target.value;
            if (props.serviceId && value) {
                const selectedDate = parseLocalDate(value);
                if (!selectedDate)
                    return;
                if (!isDateAvailable(selectedDate)) {
                    emit('update:modelValue', '');
                    emit('change', '');
                    emit('unavailable-date-selected', selectedDate);
                    showAvailabilityError.value = true;
                    availabilityErrorMessage.value =
                        t("itinerary.validation.date_unavailable", "Esta fecha no tiene disponibilidad. Por favor seleccione otra fecha.");
                    return;
                }
            }
            showAvailabilityError.value = false;
            availabilityErrorMessage.value = '';
            emit('update:modelValue', value);
            emit('change', value);
        }
        function handleMonthNavigation(year, fpMonth) {
            if (!props.serviceId)
                return;
            if (debounceTimer)
                clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
                const month = fpMonth + 1;
                const monthStatus = getMonthStatus(year, month);
                if (monthStatus === 'loaded') {
                    applyAvailabilityRules();
                    prefetchNextMonth(props.serviceId, year, month);
                    return;
                }
                isLoadingMonth.value = true;
                setCalendarLoadingState(true);
                try {
                    await ensureMonthLoaded(props.serviceId, year, month, getBookingWindowBounds());
                }
                finally {
                    isLoadingMonth.value = false;
                    setCalendarLoadingState(false);
                }
                applyAvailabilityRules();
                prefetchNextMonth(props.serviceId, year, month);
            }, 200);
        }
        function prefetchNextMonth(serviceId, year, month) {
            const nextMonth = month === 12 ? 1 : month + 1;
            const nextYear = month === 12 ? year + 1 : year;
            const bounds = getBookingWindowBounds();
            const nextMonthStart = new Date(nextYear, nextMonth - 1, 1);
            if (bounds.maxDate && nextMonthStart > bounds.maxDate)
                return;
            void ensureMonthLoaded(serviceId, nextYear, nextMonth, bounds)
                .then(() => {
                applyAvailabilityRules();
            })
                .catch(() => { });
        }
        function setCalendarLoadingState(loading) {
            const calendarEl = dateInputRef.value?._flatpickr?.calendarContainer;
            if (!calendarEl)
                return;
            if (loading) {
                calendarEl.classList.add('flatpickr-loading');
            }
            else {
                calendarEl.classList.remove('flatpickr-loading');
            }
        }
        // =============================================================================
        // LIFECYCLE HOOKS
        // =============================================================================
        function initFlatpickr() {
            if (!dateInputRef.value || props.readOnly)
                return;
            if (flatpickrInstance.value)
                return;
            if (dateInputRef.value) {
                flatpickrInstance.value = flatpickr(dateInputRef.value, {
                    dateFormat: 'Y-m-d',
                    defaultDate: props.modelValue || undefined,
                    minDate: minDate.value || undefined,
                    maxDate: maxDate.value || undefined,
                    disable: [
                        isDateDisabled
                    ],
                    onChange: (selectedDates) => {
                        if (selectedDates.length > 0) {
                            const value = formatDateForInput(selectedDates[0]);
                            handleInput({ target: { value } });
                        }
                    },
                    onMonthChange: (_dates, _str, instance) => {
                        handleMonthNavigation(instance.currentYear, instance.currentMonth);
                    },
                    onYearChange: (_dates, _str, instance) => {
                        handleMonthNavigation(instance.currentYear, instance.currentMonth);
                    },
                    onReady: () => {
                        const calendar = dateInputRef.value?.nextElementSibling;
                        if (calendar) {
                            calendar.classList.add('flatpickr-custom');
                        }
                    }
                });
            }
        }
        function destroyFlatpickr() {
            if (flatpickrInstance.value) {
                flatpickrInstance.value.destroy();
                flatpickrInstance.value = null;
            }
        }
        onMounted(() => {
            if (!props.readOnly) {
                initFlatpickr();
            }
        });
        onBeforeUnmount(() => {
            if (debounceTimer)
                clearTimeout(debounceTimer);
            destroyFlatpickr();
        });
        // Watch for modelValue changes from parent
        watch(() => props.modelValue, (newValue) => {
            if (flatpickrInstance.value && newValue !== flatpickrInstance.value.input.value) {
                flatpickrInstance.value.setDate(newValue || null, false);
            }
        });
        // Watch for min/max date changes
        watch([minDate, maxDate], ([newMin, newMax]) => {
            if (flatpickrInstance.value) {
                flatpickrInstance.value.set('minDate', newMin || undefined);
                flatpickrInstance.value.set('maxDate', newMax || undefined);
                applyAvailabilityRules();
            }
        });
        // Watch for service changes to redraw disabled dates
        watch(() => props.serviceId, () => {
            applyAvailabilityRules();
        });
        // Watch for read-only changes
        watch(() => props.readOnly, async (isReadOnly) => {
            if (isReadOnly) {
                destroyFlatpickr();
                return;
            }
            initFlatpickr();
            if (props.serviceId) {
                clearAvailability();
                hasLoadedAnyMonth.value = false;
                await loadInitialMonth(props.serviceId);
            }
        });
        function parseLocalDate(value) {
            const [year, month, day] = value.split('-').map(Number);
            if (!year || !month || !day)
                return null;
            return new Date(year, month - 1, day);
        }
        function getBookingWindowBounds() {
            return {
                minDate: props.minDays > 0 ? bookingValidationService.getEarliestBookingDate(props.minDays) : undefined,
                maxDate: props.maxDays > 0 ? bookingValidationService.getLatestBookingDate(props.maxDays) : undefined,
            };
        }
        function isDateDisabled(date) {
            if (!props.serviceId)
                return false;
            if (!isMonthLoaded(date.getFullYear(), date.getMonth() + 1))
                return true;
            return !isDateAvailable(date);
        }
        function applyAvailabilityRules() {
            if (!flatpickrInstance.value)
                return;
            flatpickrInstance.value.set('disable', [isDateDisabled]);
            flatpickrInstance.value.redraw();
        }
        async function loadInitialMonth(serviceId) {
            const selectedDate = props.modelValue
                ? parseLocalDate(props.modelValue)
                : null;
            const startDate = selectedDate ?? (props.minDays > 0
                ? bookingValidationService.getEarliestBookingDate(props.minDays)
                : new Date());
            const year = startDate.getFullYear();
            const month = startDate.getMonth() + 1;
            if (flatpickrInstance.value) {
                flatpickrInstance.value.jumpToDate(startDate);
            }
            await loadMonthAvailability(serviceId, year, month, getBookingWindowBounds());
            hasLoadedAnyMonth.value = isMonthLoaded(year, month);
            applyAvailabilityRules();
            prefetchNextMonth(serviceId, year, month);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("label", _hoisted_2, _toDisplayString(_unref(t)("itinerary.form.date", "Fecha")), 1),
                (_unref(isLoading) && !hasLoadedAnyMonth.value && !__props.readOnly)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(t)("itinerary.validation.loading_availability", "Cargando disponibilidad...")), 1))
                    : _createCommentVNode("", true),
                _withDirectives(_createElementVNode("input", {
                    ref_key: "dateInputRef",
                    ref: dateInputRef,
                    id: "date",
                    type: "text",
                    class: _normalizeClass(["w-full p-1 text-sm border border-gray-300 rounded-lg bg-gray-50 md:text-md focus:ring-blue-400", { 'border-red-400': __props.showError || showAvailabilityError.value }]),
                    value: __props.modelValue,
                    disabled: __props.readOnly || !__props.serviceId || (_unref(isLoading) && !hasLoadedAnyMonth.value),
                    placeholder: _unref(t)('itinerary.validation.select_date', 'Seleccione una fecha'),
                    readonly: ""
                }, null, 10, _hoisted_4), [
                    [_vShow, hasLoadedAnyMonth.value || !_unref(isLoading) || __props.readOnly]
                ]),
                (showAvailabilityError.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(availabilityErrorMessage.value), 1))
                    : (__props.showError && __props.errorMessage)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(__props.errorMessage), 1))
                        : (_unref(availabilityError))
                            ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_unref(availabilityError)), 1))
                            : (isLoadingMonth.value)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(_unref(t)("itinerary.validation.loading_month_availability", "Cargando disponibilidad del mes...")), 1))
                                : (bookingWindowMessage.value)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(bookingWindowMessage.value), 1))
                                    : _createCommentVNode("", true)
            ]));
        };
    }
});
